export function remembered(storage, key, now = Date.now()) {
  try {
    const expiry = Number(storage.getItem(key));
    return Number.isFinite(expiry) && expiry > now;
  } catch {
    return false;
  }
}
export function remember(storage, key, days, now = Date.now()) {
  try {
    storage.setItem(key, String(confirmationExpiry(days, now)));
    return true;
  } catch {
    return false;
  }
}
export function confirmationExpiry(days, now = Date.now()) {
  return now + Math.max(1, Math.min(365, Number(days) || 1)) * 86400000;
}
export function ageRedirect(value, base) {
  if (!value?.trim()) return null;
  try {
    const url = new URL(value, base);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}
const Base = globalThis.HTMLElement || class {};
const accepted = new Map();
export class MeriAgeGate extends Base {
  static get observedAttributes() {
    return ['data-pinned-preview'];
  }
  attributeChangedCallback() {
    this.refresh?.();
  }
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const signal = this.abort.signal;
    const dialog = this.querySelector('dialog');
    if (!dialog?.showModal) return;
    // A confirmation holds for this minimum age and remembering rule; changing either asks again.
    const key = 'meri-age:' + this.dataset.minimum + ':' + this.dataset.mode + ':' + this.dataset.days;
    const editor = this.dataset.editor === 'true';
    const pinned = () => editor && this.dataset.pinnedPreview === 'true';
    const redirect =
      this.dataset.rejectBehavior === 'redirect' ? ageRedirect(this.dataset.rejectUrl, location.origin) : null;
    const redirectError = this.querySelector('[data-age-redirect-error]');
    if (redirectError) redirectError.hidden = this.dataset.rejectBehavior !== 'redirect' || Boolean(redirect);
    let storage;
    if (!editor) {
      try {
        storage = this.dataset.mode === 'days' ? localStorage : sessionStorage;
      } catch {}
    }
    const permitted = () => {
      const mobile = matchMedia('(max-width: 749px)').matches;
      return mobile ? this.dataset.mobile === 'true' : this.dataset.desktop === 'true';
    };
    const prepareImages = () => {
      // Hidden, remembered dialogs keep lazy images. Promote only the dialog
      // actually being shown; the banner is its primary visible image.
      for (const image of dialog.querySelectorAll('img')) {
        image.loading = 'eager';
        image.fetchPriority = image.classList.contains('enhancement-age__image') ? 'high' : 'auto';
      }
    };
    // A gate that opens by itself focuses the dialog, not its first button: before any key or tap the browser would
    // ring that button as if it had been reached by keyboard (2.8.3, Owner). Screen readers start from the title and
    // the question, and Tab still reaches the buttons with their focus ring.
    const show = () => {
      prepareImages();
      dialog.showModal();
      dialog.focus();
    };
    const refresh = () => {
      if (pinned()) {
        // Editor reloads must not run native dialog focusing steps and steal
        // focus from the merchant's settings. The same dialog stays visible.
        if (dialog.matches(':modal')) dialog.close();
        prepareImages();
        dialog.open = true;
        return;
      }
      const memoryValid = Number(accepted.get(key)) > Date.now();
      if (!memoryValid) accepted.delete(key);
      if (editor || memoryValid || remembered(storage, key) || !permitted()) {
        if (dialog.open) dialog.close();
        return;
      }
      if (!dialog.open) show();
    };
    this.refresh = refresh;
    this.querySelector('[data-age-accept]').addEventListener(
      'click',
      () => {
        if (pinned()) return;
        if (!editor) {
          const days = this.dataset.mode === 'days' ? this.dataset.days : 365;
          accepted.set(key, confirmationExpiry(days));
          remember(storage, key, days);
        }
        dialog.close();
      },
      { signal },
    );
    this.querySelector('[data-age-reject]').addEventListener(
      'click',
      () => {
        // Preview must never navigate the editor or record customer confirmation.
        if (!editor && redirect) {
          location.assign(redirect);
          return;
        }
        const feedback = this.querySelector('[data-age-feedback]');
        feedback.hidden = false;
        feedback.focus();
        const preview = this.querySelector('[data-age-redirect-preview]');
        if (preview) preview.hidden = !redirect;
      },
      { signal },
    );
    // Browsers only honour preventDefault on a repeated close request after a new
    // user activation, so a second Esc (or back gesture) would dismiss the gate.
    // closedby="none" stops close requests where supported; the close handler
    // reopens an unconfirmed storefront gate everywhere else.
    if (!editor) dialog.setAttribute('closedby', 'none');
    dialog.addEventListener(
      'cancel',
      (event) => {
        if (!editor || pinned()) event.preventDefault();
      },
      { signal },
    );
    dialog.addEventListener(
      'close',
      () => {
        if (this.isConnected && (!editor || pinned())) refresh();
      },
      { signal },
    );
    this.querySelector('[data-age-preview]')?.addEventListener(
      'click',
      () => {
        if (!dialog.open) show();
      },
      { signal },
    );
    matchMedia('(max-width: 749px)').addEventListener('change', refresh, { signal });
    window.addEventListener('pageshow', refresh, { signal });
    refresh();
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.abort = null;
    this.refresh = null;
    this.querySelector('dialog')?.close();
  }
}
if (globalThis.customElements && !customElements.get('meri-age-gate'))
  customElements.define('meri-age-gate', MeriAgeGate);
